'use client';

import { HomePageBanner } from '@/models/HomePage';
import { differenceInSeconds, parseISO } from 'date-fns';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { memo, useEffect, useState } from 'react';
import SmartImage from './SmartImage';

const Countdown = dynamic(() => import('./Countdown'), {
  ssr: false,
  loading: () => null,
});

interface OptimizedBannerViewProps {
  inCarousel: boolean;
  index: number;
  banner?: HomePageBanner;
  isFirstBanner?: boolean;
  isPriority?: boolean;
  isLCP?: boolean;
  /** Desktop: fill parent height (object-cover) instead of fixed aspect ratio. */
  fillHeight?: boolean;
}

const OptimizedBannerView = ({
  banner,
  isPriority = false,
  fillHeight = false,
}: OptimizedBannerViewProps) => {
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [showCountdown, setShowCountdown] = useState(false);

  // Parse and validate countdown end date (ignore if expired/too close)
  useEffect(() => {
    const raw = banner?.badgeEndTime;
    if (!raw) return setEndDate(null);
    try {
      const d = parseISO(raw);
      const left = differenceInSeconds(d, new Date());
      setEndDate(left > 10 ? d : null);
    } catch {
      setEndDate(null);
    }
  }, [banner?.badgeEndTime]);

  // Mount Countdown shortly after the banner is ready. We intentionally do NOT
  // gate this behind requestIdleCallback: on busy pages (carousel autoplay,
  // continuous animations, etc.) the idle callback can be starved and never
  // fire, which previously left the banner visible WITHOUT its countdown
  // (notably on mobile). A small deterministic timeout keeps the main work off
  // the LCP path while guaranteeing the countdown mounts. (Countdown is already
  // a separate ssr:false dynamic chunk, so this stays light.)
  useEffect(() => {
    if (!endDate) return;
    const t = setTimeout(() => setShowCountdown(true), 200);
    return () => clearTimeout(t);
  }, [endDate]);

  const altText = banner?.alt || banner?.title || 'kampanya görseli';
  const desktopImage = banner?.imageUrl || '';
  const mobileImage = banner?.mobileImageUrl || desktopImage;

  // Render only when countdown is valid (or not used)
  if (!banner?.badgeEndTime || endDate) {
    return (
      <Link
        href={banner?.url || ''}
        className={`block w-full relative ${fillHeight ? 'md:h-full' : ''}`}
      >
        {/* CSS breakpoints instead of isDesktop state — avoids mobile→desktop image swap CLS */}
        <div
          className={`relative w-full overflow-hidden aspect-[430/242] min-[600px]:aspect-[12/5] md:rounded-2xl ${
            fillHeight
              ? 'md:aspect-auto md:absolute md:inset-0 md:h-full md:min-h-0'
              : ''
          }`}
        >
          <div className="min-[600px]:hidden">
            <Image
              src={mobileImage}
              alt={altText}
              width={430}
              height={242}
              sizes="(max-width: 390px) 390px, (max-width: 430px) 430px, 430px"
              priority={isPriority}
              loading={isPriority ? 'eager' : 'lazy'}
              className="w-full h-auto"
              quality={85}
              {...(isPriority && { fetchPriority: 'high' as const, decoding: 'auto' as const })}
            />
          </div>
          <div
            className={`hidden min-[600px]:block ${fillHeight ? 'md:absolute md:inset-0 md:h-full' : ''}`}
          >
            <SmartImage
              src={desktopImage}
              alt={altText}
              width={1200}
              height={500}
              fill={fillHeight}
              sizes={
                fillHeight
                  ? '(min-width: 768px) 75vw, 100vw'
                  : undefined
              }
              imageType="hero-desktop"
              priority={isPriority}
              loading={isPriority ? 'eager' : 'lazy'}
              {...(isPriority && { fetchPriority: 'high' as const, decoding: 'auto' as const })}
              className={`md:rounded-2xl ${
                fillHeight ? 'object-cover' : 'w-full h-auto'
              }`}
              quality={85}
              isLCP={isPriority}
              isAboveFold={isPriority}
              isCritical={isPriority}
            />
          </div>
        </div>

        {showCountdown && endDate && (
          <div className="absolute bottom-14 left-10 max-sm:bottom-0 max-sm:left-4">
            <Countdown endDate={endDate} transformOrigin="bottom left" hideHeader={true} />
          </div>
        )}
      </Link>
    );
  }

  return null;
};

export default memo(OptimizedBannerView);
